import fallbackLensIcon from "../../../assets/lenses/fallback-lens-icon.png";
import {
    type AppInfo,
    MyLensesAuthOperation,
    MyLensesPlatformType,
    MyLensesStage,
} from "../../../../gql-generated/types";
import { AppListItemModel, AppVersionStatus } from "../../models";

type OrganizationApps = Partial<AppInfo>;

export class AppInfoModel {
    public readonly iconUrl: string | null = null;
    public readonly name: string | null = null;
    public readonly prodStatus: AppVersionStatus | null = null;
    public readonly prodVersionUrl: string | null = null;
    public readonly stageStatus: AppVersionStatus | null = null;
    public readonly prodLiveVersion: string | null = null;
    public readonly stagingLiveVersion: string | null = null;
    public readonly stagingVersionUrl: string | null = null;
    public readonly appId: string | null = null;
    public readonly appUrl: string | null = null;
    public readonly stageToken: string | null = null;
    public readonly prodToken: string | null = null;
    public readonly authorizedOperations: readonly string[] | null = null;
    public readonly platformType: MyLensesPlatformType | null = null;

    public static createAppInfoItem({
        appIconUrl,
        internalAppName,
        prodLiveVersion,
        stagingLiveVersion,
        versions,
        apiTokens,
        appId,
        appAuthorizedOperations,
        platformType,
    }: OrganizationApps): AppInfoModel {
        const { token: stageToken } = AppInfoModel.getLatestToken(apiTokens, "StageStaging");
        const { token: prodToken } = AppInfoModel.getLatestToken(apiTokens, "StageProd");

        return {
            iconUrl: appIconUrl || fallbackLensIcon,
            name: internalAppName ?? "",
            prodStatus: AppListItemModel.getIsCamkitEnabled(versions, prodLiveVersion ?? -1),
            prodLiveVersion: AppListItemModel.getVersionName(versions, prodLiveVersion ?? -1) ?? null,
            prodVersionUrl: AppInfoModel.generateVersionLinkOut(appId ?? "", prodLiveVersion ?? -1),
            stagingLiveVersion: AppListItemModel.getVersionName(versions, stagingLiveVersion ?? -1) ?? null,
            stagingVersionUrl: AppInfoModel.generateVersionLinkOut(appId ?? "", stagingLiveVersion ?? -1),
            stageStatus: AppListItemModel.getIsCamkitEnabled(versions, stagingLiveVersion ?? -1),
            appUrl: AppInfoModel.generateAppLinkOut(appId ?? ""),
            stageToken: stageToken ?? null,
            prodToken: prodToken ?? null,
            appId: appId ?? "",
            authorizedOperations:
                appAuthorizedOperations
                    ?.find((op) => op.authOperation == MyLensesAuthOperation.AuthOperationSnapKitApps)
                    ?.authOperationTypes?.flatMap((authOpType) =>
                        typeof authOpType === "string" ? [authOpType] : [],
                    ) || [],
            platformType: platformType ?? null,
        };
    }

    public static getLatestToken(tokens: OrganizationApps["apiTokens"], targetStage: keyof typeof MyLensesStage) {
        if (!tokens) {
            return { token: "" };
        }
        const [token, ..._rest] = tokens
            .filter((token) => token?.stage === MyLensesStage[targetStage])
            .sort((a, b) => (a?.createdTsMillis ?? 0) - (b?.createdTsMillis ?? 0));

        return token ?? { token: "" };
    }

    public static generateVersionLinkOut(appId: string, version: number) {
        return `https://kit.snapchat.com/manage/apps/${appId}/version/${version}`;
    }

    public static generateAppLinkOut(appId: string) {
        return `https://kit.snapchat.com/manage/apps/${appId}`;
    }
}
